import React, { useState } from "react";
import { Calculator, Loader2, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { buildCostEstimatePDF } from "../lib/pdf";
import { toast } from "sonner";
import Modal, { ModalHeader } from "./Modal";

/**
 * Cost-estimate PDF button. Opens a portaled modal, builds the PDF, triggers
 * a local browser download, then archives the raw PDF bytes to the backend so
 * other teammates and auditors can later verify the exact file that was issued.
 */
export default function CostEstimateButton({ projectId, project, tasks, roles, timeUnit, authorName, savePathHint, onArchived }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle"); // idle | running | archived | error
  const [err, setErr] = useState("");
  const [lastId, setLastId] = useState("");

  const close = () => { setOpen(false); setState("idle"); setErr(""); };

  const run = async () => {
    setOpen(true); setState("running"); setErr("");
    try {
      const { doc, totals } = await buildCostEstimatePDF({ project, tasks, roles, timeUnit, authorName });
      const slug = (project.name || "project").replace(/[^a-z0-9]+/gi, "_");
      const ymd = new Date().toISOString().slice(0, 10);
      const filename = `${slug}_cost-estimate_${ymd}.pdf`;

      // 1) Local browser download (goes to the browser's default Downloads
      //    folder — users can map that to the "save path" they set).
      doc.save(filename);

      // 2) Archive the bytes on the backend so other teammates / auditors can
      //    retrieve the exact PDF later.
      const blob = doc.output("blob");
      const b64 = await blobToBase64(blob);
      const { data } = await api.post(`/projects/${projectId}/cost-estimates`, {
        filename,
        pdf_base64: b64,
        metrics: totals,
      });
      setLastId(data?.id || "");
      setState("archived");
      onArchived && onArchived();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Something went wrong.");
      setState("error");
      toast.error("Cost estimate failed");
    }
  };

  return (
    <>
      <button
        onClick={run}
        disabled={state === "running"}
        data-testid="cost-estimate-btn"
        className="inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
      >
        {state === "running" ? <Loader2 className="animate-spin" size={14}/> : <Calculator size={14}/>} Cost estimate PDF
      </button>
      <Modal open={open} onClose={close} testId="cost-estimate-modal">
        <ModalHeader icon={Calculator} title="Cost estimate PDF" onClose={close} testId="cost-estimate-close"/>
        {state === "running" && (
          <div className="flex items-center gap-3 py-6 text-slate-200" data-testid="cost-estimate-running">
            <Loader2 className="animate-spin text-emerald-400" size={18}/>
            <div>
              <div className="text-sm font-medium">Building and archiving your cost estimate…</div>
              <div className="text-xs text-slate-400 mt-0.5">Tasks, hours, planned cost per task, and a grand total.</div>
            </div>
          </div>
        )}
        {state === "archived" && (
          <>
            <div className="flex items-center gap-3 py-4" data-testid="cost-estimate-archived">
              <CheckCircle2 className="text-emerald-400" size={24}/>
              <div>
                <div className="text-sm text-white font-medium">Downloaded + archived</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  The PDF saved to your browser's Downloads folder{savePathHint ? ` (set to ${savePathHint})` : ""} and a signed copy was stored in the project archive so teammates and auditors can verify it.
                </div>
                {lastId && <div className="text-[11px] text-slate-500 mt-1 font-mono">archive id: {lastId}</div>}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
              <button onClick={run} data-testid="cost-estimate-rebuild" className="border border-slate-700 hover:border-blue-500 text-slate-200 text-sm px-3 py-2 rounded-lg">Build another</button>
              <button onClick={close} className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-2 rounded-lg">Done</button>
            </div>
          </>
        )}
        {state === "error" && (
          <>
            <div className="text-sm text-rose-300 py-4" data-testid="cost-estimate-error">{err}</div>
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
              <button onClick={close} className="text-sm text-slate-400 hover:text-white px-3 py-2">Close</button>
              <button onClick={run} className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-2 rounded-lg">Retry</button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
